import React from 'react';
import { useSelector, useStore } from 'react-redux';

import type { Reference } from './ReferenceMentionNode';
import { referencesFromVideos, VIDEO_PAGE_SIZE } from './videoReferences';

import type { Blueprint } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { fetchCustomBlueprints, getCustomBlueprintsList } from 'js/src/data/blueprints';
import { getAllBrandKits, getBrandKitColorsInHex, loadBrandKits } from 'js/src/data/brand-kits';
import { getAIVoiceById, getAIVoiceIds } from 'js/src/data/ai-voices-and-avatars';
import { getTemplateById, getTemplateIds } from 'js/src/data/templates';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import { getApproximateTimeFromSeconds } from 'js/src/libs/video-duration-tools';

/** The defaults a blueprint would apply to a new video, as its tiles show them. */
function describeBlueprint(blueprint: Blueprint): string {
    const parts: string[] = [];

    if (blueprint.default_target_duration_seconds) {
        parts.push(getApproximateTimeFromSeconds(blueprint.default_target_duration_seconds));
    }
    if (blueprint.default_aspect_ratio) {
        parts.push(blueprint.default_aspect_ratio);
    }

    return parts.join(' · ');
}

/**
 * The things a chat message can `@`-reference. `enabled` is false on surfaces
 * that can't show mentions, so they fetch nothing.
 *
 * Videos are fetched here rather than read from redux, whose video list is
 * scoped to the dashboard's active view — which need not be a list of videos.
 */
export function useReferenceOptions(enabled = true): Reference[] {
    return useReferenceOptionsWithStatus(enabled).references;
}

/**
 * The same options, along with whether the user's own videos and blueprints
 * have finished loading — for a surface that has to tell "none" apart from
 * "not yet".
 */
export function useReferenceOptionsWithStatus(enabled = true): {
    references: Reference[];
    haveVideosAndBlueprintsLoaded: boolean;
} {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const [videoReferences, setVideoReferences] = React.useState<Reference[]>([]);
    const [haveVideosLoaded, setHaveVideosLoaded] = React.useState(false);
    const [haveBlueprintsLoaded, setHaveBlueprintsLoaded] = React.useState(false);

    const brandKits = useSelector(getAllBrandKits);
    const blueprints = useSelector(getCustomBlueprintsList);
    const templateIds = useSelector(getTemplateIds);
    const voiceIds = useSelector(getAIVoiceIds);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }
        dispatch(loadBrandKits());
        let cancelled = false;
        void dispatch(fetchCustomBlueprints()).then(() => {
            if (!cancelled) {
                setHaveBlueprintsLoaded(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [dispatch, enabled]);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const { data } = await ApiService.videoList({
                    query: {
                        page: 1,
                        page_size: VIDEO_PAGE_SIZE,
                        only_archived: false,
                        sort_by: 'last_updated_date',
                    },
                });
                if (cancelled) {
                    return;
                }
                setVideoReferences(referencesFromVideos(data?.results ?? []));
            } catch {
                // The other reference kinds still work without videos.
            }
            if (!cancelled) {
                setHaveVideosLoaded(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [enabled]);

    // Mapped here rather than in a selector, which would return a new array
    // each call.
    const templateReferences = React.useMemo(() => {
        const state = store.getState();
        return templateIds.toArray().reduce<Reference[]>((acc, templateId) => {
            const template = getTemplateById(state, templateId);
            const name = template?.get('display_name');
            if (name) {
                acc.push({
                    kind: 'template',
                    id: templateId,
                    name,
                    thumbUrl: template?.get('image_url') ?? null,
                });
            }
            return acc;
        }, []);
    }, [templateIds, store]);

    // Voices are loaded per slug on demand elsewhere, so the store usually holds
    // only the few already referenced; the full catalogue isn't fetched here.
    const voiceReferences = React.useMemo(() => {
        const state = store.getState();
        return voiceIds.reduce<Reference[]>((acc, voiceId) => {
            const voice = getAIVoiceById(state, voiceId);
            if (voice && !voice.is_deprecated) {
                acc.push({
                    kind: 'voice',
                    id: voice.slug,
                    name: voice.display_name,
                    detail: voice.attributes?.accent?.[0] ?? null,
                    previewUrl: voice.preview_url,
                });
            }
            return acc;
        }, []);
    }, [voiceIds, store]);

    const blueprintReferences = React.useMemo(
        () =>
            blueprints.map((blueprint) => ({
                kind: 'blueprint' as const,
                id: String(blueprint.id),
                name: blueprint.name,
                thumbUrl: blueprint.poster_url,
                detail: describeBlueprint(blueprint),
            })),
        [blueprints],
    );

    const brandKitReferences = React.useMemo(() => {
        const state = store.getState();
        return brandKits.toArray().map((brandKit) => {
            const id = brandKit.get('id');
            const colors = getBrandKitColorsInHex(state, id);
            return {
                kind: 'brand-kit' as const,
                id: String(id),
                name: brandKit.get('name'),
                swatches: colors ? colors.toArray() : undefined,
            };
        });
    }, [brandKits, store]);

    const references = React.useMemo(
        () => [
            ...videoReferences,
            ...blueprintReferences,
            ...templateReferences,
            ...brandKitReferences,
            ...voiceReferences,
        ],
        [
            videoReferences,
            blueprintReferences,
            templateReferences,
            brandKitReferences,
            voiceReferences,
        ],
    );

    return {
        references,
        haveVideosAndBlueprintsLoaded: haveVideosLoaded && haveBlueprintsLoaded,
    };
}
